import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import AdminHeader from '../../components/admin/AdminHeader';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { adminAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { isAdminRole } from '../../utils/roles';

const defaultFormState = {
  name: '',
  description: '',
  color: '#f97316',
  iconSvg: '🎟️',
  isActive: true
};

const AdminCategoriesPage = () => {
  const { user: currentUser } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCategories: 0
  });

  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formState, setFormState] = useState(defaultFormState);
  const [actionLoading, setActionLoading] = useState(false);

  const isMainAdmin = isAdminRole(currentUser?.role);

  const notify = (options) => {
    Swal.fire({
      confirmButtonColor: '#ea580c',
      ...options
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: pagination.currentPage,
        limit: 20,
        ...(debouncedSearchQuery && { search: debouncedSearchQuery }),
        ...(statusFilter !== 'all' && { status: statusFilter })
      };

      const data = await adminAPI.getCategories(params);
      setCategories(data.data.categories || []);
      setPagination(data.data.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalCategories: 0
      });
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError(err.message || 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, statusFilter, pagination.currentPage]);

  const openCreateModal = () => {
    if (!isAdminRole(currentUser?.role)) {
      notify({
        icon: 'error',
        title: 'Insufficient permissions',
        text: 'Only main admins can perform this action.'
      });
      return;
    }
    setSelectedCategory(null);
    setFormState(defaultFormState);
    setShowFormModal(true);
  };

  const openEditModal = (category) => {
    if (!isAdminRole(currentUser?.role)) {
      notify({
        icon: 'error',
        title: 'Insufficient permissions',
        text: 'Only main admins can perform this action.'
      });
      return;
    }
    setSelectedCategory(category);
    setFormState({
      name: category.name || '',
      description: category.description || '',
      color: category.color || '#f97316',
      iconSvg: category.iconSvg || '🎟️',
      isActive: category.isActive ?? true
    });
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setSelectedCategory(null);
    setFormState(defaultFormState);
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();

    if (!formState.name.trim()) {
      notify({
        icon: 'warning',
        title: 'Name required',
        text: 'Please enter a category name before saving.'
      });
      return;
    }

    if (!isAdminRole(currentUser?.role)) {
      notify({
        icon: 'error',
        title: 'Insufficient permissions',
        text: 'Only main admins can perform this action.'
      });
      return;
    }

    try {
      setActionLoading(true);

      const isEdit = Boolean(selectedCategory);
      const payload = {
        name: formState.name.trim(),
        description: formState.description.trim(),
        color: formState.color,
        iconSvg: formState.iconSvg,
        isActive: formState.isActive
      };

      const data = isEdit
        ? await adminAPI.updateCategory(selectedCategory.id, payload)
        : await adminAPI.createCategory(payload);

      notify({
        icon: 'success',
        title: isEdit ? 'Category updated' : 'Category created',
        text: data.message || `Category ${isEdit ? 'updated' : 'created'} successfully.`
      });

      closeFormModal();
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
      fetchCategories();
    } catch (err) {
      console.error('Error saving category:', err);
      notify({
        icon: 'error',
        title: 'Failed to save category',
        text: err.message || 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleCategoryStatus = async (category) => {
    if (!isAdminRole(currentUser?.role)) {
      notify({
        icon: 'error',
        title: 'Insufficient permissions',
        text: 'Only main admins can perform this action.'
      });
      return;
    }

    try {
      setActionLoading(true);

      const data = await adminAPI.updateCategory(category.id, { isActive: !category.isActive });

      notify({
        icon: 'success',
        title: 'Status updated',
        text: `Category marked as ${!category.isActive ? 'active' : 'inactive'}.`
      });

      fetchCategories();
    } catch (err) {
      console.error('Error toggling category status:', err);
      notify({
        icon: 'error',
        title: 'Failed to update status',
        text: err.message || 'An unexpected error occurred.'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const renderIconPreview = (iconValue) => {
    const trimmed = (iconValue || '🎟️').trim();
    if (trimmed.startsWith('<svg')) {
      return <span className="w-6 h-6 text-white" dangerouslySetInnerHTML={{ __html: trimmed }} />;
    }
    return <span className="text-xl leading-none">{trimmed}</span>;
  };

  const getStatusBadge = (isActive) => {
    return isActive
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
  };

  const stats = {
    total: pagination.totalCategories,
    active: categories.filter((category) => category.isActive).length,
    inactive: categories.filter((category) => !category.isActive).length,
    withEvents: categories.filter((category) => (category._count?.events || 0) > 0).length
  };

  const handleChangePage = (nextPage) => {
    setPagination((prev) => ({ ...prev, currentPage: nextPage }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
      <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col ml-0 lg:ml-72">
        <AdminHeader onMenuClick={() => setSidebarOpen(true)} />

        <main className="flex-1 p-6 lg:p-8 w-full overflow-y-auto">
          <div className="mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
                <i className="fas fa-tags mr-3 text-orange-500"></i>
                Category Management
              </h1>
              <p className="text-gray-600">Organize, curate, and refine event categories for the marketplace</p>
              {!isMainAdmin && (
                <p className="mt-2 text-sm text-red-500 font-medium">
                  Viewing only. Please contact the main admin to make changes.
                </p>
              )}
            </div>
            <button
              onClick={openCreateModal}
              disabled={!isMainAdmin}
              className={`inline-flex items-center px-5 py-3 rounded-xl shadow-md transition-colors ${
                isMainAdmin
                  ? 'bg-orange-600 text-white hover:bg-orange-700'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
              title={isMainAdmin ? 'Create a new category' : 'Only main admins can add categories'}
            >
              <i className="fas fa-plus mr-2"></i>
              Add Category
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-orange-500">
              <p className="text-sm font-medium text-gray-600 mb-1">Total Categories</p>
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-gray-900">{stats.total}</span>
                <span className="w-12 h-12 bg-orange-100 rounded-md flex items-center justify-center text-orange-600 text-xl">
                  <i className="fas fa-th-large"></i>
                </span>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
              <p className="text-sm font-medium text-gray-600 mb-1">Active Categories</p>
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-gray-900">{stats.active}</span>
                <span className="w-12 h-12 bg-green-100 rounded-md flex items-center justify-center text-green-600 text-xl">
                  <i className="fas fa-check-circle"></i>
                </span>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-red-500">
              <p className="text-sm font-medium text-gray-600 mb-1">Inactive Categories</p>
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-gray-900">{stats.inactive}</span>
                <span className="w-12 h-12 bg-red-100 rounded-md flex items-center justify-center text-red-600 text-xl">
                  <i className="fas fa-ban"></i>
                </span>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-indigo-500">
              <p className="text-sm font-medium text-gray-600 mb-1">Categories with Events</p>
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-gray-900">{stats.withEvents}</span>
                <span className="w-12 h-12 bg-indigo-100 rounded-md flex items-center justify-center text-indigo-600 text-xl">
                  <i className="fas fa-ticket-alt"></i>
                </span>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  value={searchQuery}
                  placeholder="Search categories by name or description..."
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                    setPagination((prev) => ({ ...prev, currentPage: 1 }));
                  }}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <select
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  setPagination((prev) => ({ ...prev, currentPage: 1 }));
                }}
                className="px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              <button
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('all');
                  setPagination((prev) => ({ ...prev, currentPage: 1 }));
                }}
                className="px-4 py-2.5 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <i className="fas fa-redo mr-2"></i>
                Reset Filters
              </button>
            </div>
          </div>

          {/* Categories Table */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent mx-auto mb-4"></div>
                <p className="text-gray-600">Loading categories...</p>
              </div>
            ) : error ? (
              <div className="p-12 text-center">
                <i className="fas fa-exclamation-circle text-red-500 text-4xl mb-4"></i>
                <p className="text-red-600 font-medium">{error}</p>
              </div>
            ) : categories.length === 0 ? (
              <div className="p-12 text-center">
                <i className="fas fa-folder-open text-gray-400 text-5xl mb-4"></i>
                <p className="text-gray-600 font-medium">No categories found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Events</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {categories.map((category) => (
                        <tr key={category.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <span
                                className="w-12 h-12 rounded-md flex items-center justify-center text-white shadow"
                                style={{ backgroundColor: category.color || '#ea580c' }}
                              >
                                {renderIconPreview(category.iconSvg)}
                              </span>
                              <div>
                                <p className="font-semibold text-gray-900">{category.name}</p>
                                <p className="text-xs text-gray-500">ID: {category.id}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 max-w-md">
                            <p className="text-sm text-gray-600 line-clamp-2">
                              {category.description || 'No description provided'}
                            </p>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-700 font-semibold">
                              {category._count?.events || 0}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 text-xs font-semibold rounded-md ${getStatusBadge(category.isActive)}`}>
                              {category.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                            {category.createdAt ? new Date(category.createdAt).toLocaleDateString('en-KE', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            }) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => openEditModal(category)}
                                disabled={!isMainAdmin}
                                className={`p-2 rounded-lg transition-colors ${
                                  isMainAdmin
                                    ? 'text-indigo-600 hover:bg-indigo-50'
                                    : 'text-gray-400 cursor-not-allowed'
                                }`}
                                title={isMainAdmin ? 'Edit category' : 'Admin only'}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                onClick={() => handleToggleCategoryStatus(category)}
                                disabled={actionLoading || !isMainAdmin}
                                className={`p-2 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                                  category.isActive
                                    ? 'text-amber-600 hover:bg-amber-50'
                                    : 'text-green-600 hover:bg-green-50'
                                }`}
                                title={isMainAdmin ? (category.isActive ? 'Deactivate' : 'Activate') : 'Admin only'}
                              >
                                <i className={`fas fa-${category.isActive ? 'ban' : 'check'}`}></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {pagination.totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                    <p className="text-sm text-gray-600">
                      Showing {(pagination.currentPage - 1) * 20 + 1} to {Math.min(pagination.currentPage * 20, pagination.totalCategories)} of {pagination.totalCategories} categories
                    </p>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleChangePage(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 1}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="px-4 py-2 text-sm font-medium text-gray-700">
                        Page {pagination.currentPage} of {pagination.totalPages}
                      </span>
                      <button
                        onClick={() => handleChangePage(pagination.currentPage + 1)}
                        disabled={pagination.currentPage === pagination.totalPages}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {/* Create / Edit Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {selectedCategory ? 'Edit Category' : 'Create Category'}
                </h3>
                <p className="text-sm text-gray-500">
                  Provide category details for the events marketplace
                </p>
              </div>
              <button
                onClick={closeFormModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSubmitForm} className="px-6 py-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={formState.name}
                    onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
                    placeholder="e.g. Music, Business, Sports"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Icon (emoji or SVG markup)
                  </label>
                  <textarea
                    value={formState.iconSvg}
                    onChange={(event) => setFormState((prev) => ({ ...prev, iconSvg: event.target.value }))}
                    rows={3}
                    placeholder="🎵 or <svg ...></svg>"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  <div className="mt-3 flex items-center space-x-3 text-sm text-gray-500">
                    <span>Preview:</span>
                    <span className="w-10 h-10 rounded-md flex items-center justify-center bg-gray-100">
                      {renderIconPreview(formState.iconSvg)}
                    </span>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formState.description}
                    onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))}
                    rows={3}
                    placeholder="Describe what makes events in this category special."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Color
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={formState.color}
                      onChange={(event) => setFormState((prev) => ({ ...prev, color: event.target.value }))}
                      className="w-16 h-12 border border-gray-200 rounded-md"
                    />
                    <input
                      type="text"
                      value={formState.color}
                      onChange={(event) => setFormState((prev) => ({ ...prev, color: event.target.value }))}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Status
                  </label>
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={() => setFormState((prev) => ({ ...prev, isActive: !prev.isActive }))}
                      className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                        formState.isActive
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {formState.isActive ? 'Active' : 'Inactive'}
                    </button>
                    <span className="text-sm text-gray-500">
                      {formState.isActive
                        ? 'Category will be visible to customers.'
                        : 'Category will be hidden from the storefront.'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeFormModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="px-5 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                >
                  {actionLoading ? 'Saving...' : selectedCategory ? 'Update Category' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminCategoriesPage;


